import React, { useState, useEffect } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons,
  IonBackButton, IonButton, IonSpinner,
} from '@ionic/react';
import { useHistory, useParams } from 'react-router-dom';
import { usePreferredGames } from '../hooks/useGames';
import { useBinder, useCreateBinder, useUpdateBinder } from '../hooks/useBinders';

const COLORS = ['#3880ff', '#5260ff', '#2dd36f', '#ffc409', '#eb445a', '#3dc2ff', '#92949c', '#f4a261'];

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, fontSize: 14,
  fontWeight: 600, color: 'var(--ion-color-medium)',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', fontSize: 16,
  border: '1px solid var(--ion-color-medium)', borderRadius: 8,
  background: 'var(--ion-item-background, #fff)',
  color: 'var(--ion-text-color)',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = { ...inputStyle, appearance: 'auto' as any };

const fieldGroup: React.CSSProperties = { marginBottom: 12 };

const BinderForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'new';
  const history = useHistory();
  const { data: games = [] } = usePreferredGames();
  const { data: existingBinder } = useBinder(isEdit ? Number(id) : 0);
  const createBinder = useCreateBinder();
  const updateBinder = useUpdateBinder();

  const [form, setForm] = useState({
    name: '',
    description: '',
    color: '#3880ff',
    icon: 'albums',
    game_id: null as number | null,
  });

  useEffect(() => {
    if (isEdit && existingBinder) {
      setForm({
        name: existingBinder.name,
        description: existingBinder.description || '',
        color: existingBinder.color,
        icon: existingBinder.icon,
        game_id: existingBinder.game_id || null,
      });
    }
  }, [isEdit, existingBinder]);

  const handleSubmit = async () => {
    const payload = {
      ...form,
      game_id: form.game_id || undefined,
    };
    if (isEdit) {
      await updateBinder.mutateAsync({ id: Number(id), data: payload });
    } else {
      await createBinder.mutateAsync(payload);
    }
    history.goBack();
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start"><IonBackButton defaultHref="/binders" /></IonButtons>
          <IonTitle>{isEdit ? 'Edit Binder' : 'New Binder'}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div style={{ maxWidth: 500, margin: '0 auto' }}>
          <div style={fieldGroup}>
            <label style={labelStyle}>Binder Name *</label>
            <input style={inputStyle} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Description</label>
            <textarea
              style={{ ...inputStyle, resize: 'vertical', minHeight: 80, fontFamily: 'inherit' }}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Color</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              {COLORS.map((c) => (
                <div
                  key={c}
                  onClick={() => setForm((f) => ({ ...f, color: c }))}
                  style={{
                    width: 36, height: 36, borderRadius: '50%', backgroundColor: c,
                    border: form.color === c ? '3px solid #222' : '2px solid transparent',
                    cursor: 'pointer',
                  }}
                />
              ))}
            </div>
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Game (optional filter)</label>
            <select
              style={selectStyle}
              value={form.game_id ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, game_id: e.target.value ? Number(e.target.value) : null }))}
            >
              <option value="">All Games</option>
              {games.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>

          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={!form.name || createBinder.isPending || updateBinder.isPending}
            style={{ marginTop: 16 }}
          >
            {(createBinder.isPending || updateBinder.isPending) ? <IonSpinner name="crescent" /> : (isEdit ? 'Update Binder' : 'Create Binder')}
          </IonButton>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default BinderForm;
